import {
    createContext,
    useContext,
    useId,
    useState,
    type ComponentPropsWithRef,
    type ComponentType,
    type FormEventHandler,
    type ReactNode,
    type SVGProps,
} from 'react';
import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/shared-ui/atoms/button';
import { Icon } from '@/shared-ui/atoms/icon';
import { SingleInput } from '@/shared-ui/atoms/single-input';
import { Slug } from '@/shared-ui/atoms/slug';
import { Typography } from '@/shared-ui/atoms/typography';

type BenefitIcon = ComponentType<SVGProps<SVGSVGElement>>;

type BenefitsCardDisclosureContextValue = {
    isExpandable: boolean;
    isExpanded: boolean;
    contentId: string;
    toggleExpanded: () => void;
};

const BenefitsCardDisclosureContext = createContext<BenefitsCardDisclosureContextValue | null>(null);

function useBenefitsCardDisclosure(componentName: string): BenefitsCardDisclosureContextValue {
    const context = useContext(BenefitsCardDisclosureContext);

    if (!context) {
        throw new Error(`${componentName} must be used within BenefitsCard.Root`);
    }

    return context;
}

type RootProps = ComponentPropsWithRef<'section'> & {
    isExpandable?: boolean;
    defaultExpanded?: boolean;
};

const Root = ({ isExpandable = false, defaultExpanded = true, className, ref, children, ...props }: RootProps) => {
    const contentId = useId();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const contextValue: BenefitsCardDisclosureContextValue = {
        isExpandable,
        isExpanded,
        contentId,
        toggleExpanded: () => setIsExpanded((currentValue) => !currentValue),
    };

    return (
        <BenefitsCardDisclosureContext.Provider value={contextValue}>
            <section
                ref={ref}
                data-slot="benefits-card"
                className={cn('rounded-[8px] bg-background p-4 flex gap-6 flex-col', className)}
                {...props}>
                {children}
            </section>
        </BenefitsCardDisclosureContext.Provider>
    );
};
Root.displayName = 'BenefitsCard.Root';

type HeaderProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    icon: BenefitIcon;
    children?: ReactNode;
};

const Header = ({ icon, className, children, ref, ...props }: HeaderProps) => (
    <div
        ref={ref}
        data-slot="benefits-card-header"
        className={cn('flex justify-center relative', className)}
        {...props}>
        <Icon icon={icon} width={142} height={86} className="text-primary" />
        {children}
    </div>
);
Header.displayName = 'BenefitsCard.Header';

type ToggleButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'type' | 'onClick'> & {
    expandedIcon: BenefitIcon;
    collapsedIcon: BenefitIcon;
    expandedLabel: string;
    collapsedLabel: string;
};

const ToggleButton = ({
    expandedIcon,
    collapsedIcon,
    expandedLabel,
    collapsedLabel,
    className,
    ref,
    ...props
}: ToggleButtonProps) => {
    const { isExpandable, isExpanded, contentId, toggleExpanded } =
        useBenefitsCardDisclosure('BenefitsCard.ToggleButton');

    if (!isExpandable) {
        return null;
    }

    return (
        <button
            ref={ref}
            type="button"
            data-slot="benefits-card-toggle-button"
            aria-controls={contentId}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? expandedLabel : collapsedLabel}
            className={cn(
                'flex justify-center size-8 items-center absolute -right-2 -top-2 rounded-full bg-snk-ndulge',
                className
            )}
            onClick={toggleExpanded}
            {...props}>
            <Icon icon={isExpanded ? expandedIcon : collapsedIcon} className="size-4 text-snk-white" />
        </button>
    );
};
ToggleButton.displayName = 'BenefitsCard.ToggleButton';

type CollapsibleContentProps = ComponentPropsWithRef<'div'>;

const CollapsibleContent = ({ className, ref, children, ...props }: CollapsibleContentProps) => {
    const { isExpandable, isExpanded, contentId } = useBenefitsCardDisclosure('BenefitsCard.CollapsibleContent');

    if (isExpandable && !isExpanded) {
        return null;
    }

    return (
        <div
            ref={ref}
            id={isExpandable ? contentId : undefined}
            data-slot="benefits-card-collapsible-content"
            className={cn('contents', className)}
            {...props}>
            {children}
        </div>
    );
};
CollapsibleContent.displayName = 'BenefitsCard.CollapsibleContent';

type HeadingProps = ComponentPropsWithRef<'p'>;

const Heading = ({ className, ref, ...props }: HeadingProps) => (
    <Typography
        ref={ref}
        data-slot="benefits-card-heading"
        as="p"
        className={cn('text-center tracking-[1.28px] uppercase', className)}
        {...props}
    />
);
Heading.displayName = 'BenefitsCard.Heading';

type GridProps = ComponentPropsWithRef<'div'>;

const Grid = ({ className, ref, ...props }: GridProps) => (
    <div ref={ref} data-slot="benefits-card-grid" className={cn('grid gap-4 grid-cols-2', className)} {...props} />
);
Grid.displayName = 'BenefitsCard.Grid';

type ItemProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    icon: BenefitIcon;
    children: ReactNode;
};

const Item = ({ icon, className, children, ref, ...props }: ItemProps) => (
    <div ref={ref} data-slot="benefits-card-item" className={cn('space-y-2 text-center', className)} {...props}>
        <div className="flex justify-center">
            <Slug icon={icon} size="md" type="1" />
        </div>
        <Typography as="p" className="text-center text-[12px] tracking-[0.24px] leading-[16px]">
            {children}
        </Typography>
    </div>
);
Item.displayName = 'BenefitsCard.Item';

type BirthdayFormProps = Omit<ComponentPropsWithRef<'div'>, 'children' | 'onSubmit'> & {
    day: string;
    month: string;
    year: string;
    title?: string;
    subtitle?: string;
    dayLabel?: string;
    monthLabel?: string;
    yearLabel?: string;
    onDayChange: (value: string) => void;
    onMonthChange: (value: string) => void;
    onYearChange: (value: string) => void;
    onSubmit?: FormEventHandler<HTMLFormElement>;
    formClassName?: string;
};

const BirthdayForm = ({
    day,
    month,
    year,
    dayLabel = 'Day',
    monthLabel = 'Month',
    title = 'Date of Birth',
    subtitle = 'we’d love to send you a gift!',
    yearLabel = 'Year',
    onDayChange,
    onMonthChange,
    onYearChange,
    onSubmit,
    className,
    formClassName,
    ref,
    ...props
}: BirthdayFormProps) => (
    <div ref={ref} data-slot="benefits-card-birthday-form" className={cn(className)} {...props}>
        <Typography as="p">
            {title} <span className="text-[12px]">{subtitle}</span>
        </Typography>

        <form
            data-slot="benefits-card-birthday-form-fields"
            className={cn('mt-2 flex items-center gap-2 text-base', formClassName)}
            onSubmit={onSubmit}>
            <SingleInput
                className="text-base"
                aria-label={dayLabel}
                placeholder="DD"
                inputMode="numeric"
                maxLength={2}
                value={day}
                onChange={(event) => onDayChange(event.target.value)}
            />
            <SingleInput
                className="text-base"
                aria-label={monthLabel}
                placeholder="MM"
                inputMode="numeric"
                maxLength={2}
                value={month}
                onChange={(event) => onMonthChange(event.target.value)}
            />
            <SingleInput
                className="text-base"
                aria-label={yearLabel}
                placeholder="YYYY"
                inputMode="numeric"
                maxLength={4}
                value={year}
                onChange={(event) => onYearChange(event.target.value)}
            />

            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
    </div>
);
BirthdayForm.displayName = 'BenefitsCard.BirthdayForm';

type SubmitButtonProps = ButtonProps;

const SubmitButton = ({ className, size = 'lg', ...props }: SubmitButtonProps) => (
    <Button className={cn('w-full uppercase tracking-[4.8px]', className)} size={size} {...props} />
);
SubmitButton.displayName = 'BenefitsCard.SubmitButton';

type ErrorMessageProps = ComponentPropsWithRef<'p'>;

const ErrorMessage = ({ className, ref, ...props }: ErrorMessageProps) => (
    <Typography
        ref={ref}
        data-slot="benefits-card-error-message"
        as="p"
        className={cn('mt-2 text-center text-destructive text-[12px] leading-4', className)}
        {...props}
    />
);
ErrorMessage.displayName = 'BenefitsCard.ErrorMessage';

type FooterCopyProps = ComponentPropsWithRef<'p'>;

const FooterCopy = ({ className, ref, ...props }: FooterCopyProps) => (
    <Typography
        ref={ref}
        data-slot="benefits-card-footer-copy"
        as="p"
        weight="light"
        className={cn('text-center mt-2 text-[12px] tracking-[0.24px]', className)}
        {...props}
    />
);
FooterCopy.displayName = 'BenefitsCard.FooterCopy';

const BenefitsCard = {
    Root,
    Header,
    ToggleButton,
    CollapsibleContent,
    Heading,
    Grid,
    Item,
    BirthdayForm,
    SubmitButton,
    ErrorMessage,
    FooterCopy,
} as const;

export { BenefitsCard };
